import { useCallback, useMemo } from "react";
export interface Validation {
  valid: boolean;
  type: keyof ValidityState;
  message?: string;
}

const selectValidityType = (validity: ValidityState) => {
  let type = "valid";
  for (const key in validity) {
    if (validity[key as keyof ValidityState] === true) {
      type = key;
      break;
    }
  }
  return type as keyof ValidityState;
};

const isValidMessage = (
  message: string | false | undefined,
): message is string => {
  return message !== "" && message !== false && message !== undefined;
};

export function useValidation<T>({
  ref,
  validationMessages,
  customValidations,
  value,
}: {
  ref: React.MutableRefObject<
    HTMLInputElement | HTMLTextAreaElement | null | undefined
  >;
  validationMessages?: { [key in keyof ValidityState]?: string };
  value: T;
  customValidations?: ((value: T) => string | false | undefined)[];
}) {
  const selectedCustomValidation = useMemo(() => {
    return customValidations?.find((customValidation) => {
      return isValidMessage(customValidation(value));
    });
  }, [customValidations, value]);

  if (selectedCustomValidation) {
    const message = selectedCustomValidation(value);
    if (isValidMessage(message)) {
      // eslint-disable-next-line react-hooks/refs
      ref.current?.setCustomValidity(message);
    }
  } else {
    // 無効なものがなければリセット
    // eslint-disable-next-line react-hooks/refs
    ref.current?.setCustomValidity("");
  }

  const selectValidationMessage = useCallback(
    (type: keyof ValidityState, validationMessage?: string) => {
      if (validationMessages?.[type]) {
        return validationMessages[type];
      } else {
        return validationMessage;
      }
    },
    [validationMessages],
  );

  // eslint-disable-next-line react-hooks/refs
  const element = ref.current;
  const validity = element?.validity;
  // eslint-disable-next-line react-hooks/refs
  const validationMessage = element?.validationMessage;

  const validation = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    const type = validity ? selectValidityType(validity) : "valid";

    // eslint-disable-next-line react-hooks/refs
    const message = selectValidationMessage(type, validationMessage);
    return {
      value,
      // eslint-disable-next-line react-hooks/refs
      valid: validity?.valid,
      message,
      type,
    };
  }, [selectValidationMessage, validationMessage, validity, value]);

  // eslint-disable-next-line react-hooks/refs
  return validation;
}
